// recovery-after-attempt-stop.ts
//
// Orchestrator recovery (3.40.0, D2.7): at the end of an attempt that STOPPED
// its ticket, write the recovery ticket in the same tick — after the
// post-route worktree reset (security audit M-1) — and hand the tick
// result its two fields. The trigger is matched by the seq journalAttemptOutcome
// returned (never `store.state.last_event_seq`: usage was committed after — FN1).
// A throw here never fails the attempt (usage is already recorded): it is
// reported and the next tick's scan resumes from the journal.
import type { RecoverySkipReason } from "./recovery-guards.js";
import { materializePendingRecoveries } from "./recovery-ticket-materializer.js";
import type { SupervisorTickInput } from "./supervisor-loop-types.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

export interface RecoveryTickFields {
  recovery_ticket?: string;
  recovery_skipped_reason?: RecoverySkipReason;
}

function stoppedTicket(route: RouteOutcomeResult): boolean {
  return (
    route.kind === "retry_decision" &&
    (route.decision.outcome === "blocked" || route.decision.outcome === "escalate_waiting_human")
  );
}

export async function materializeRecoveryAfterStop(
  tickInput: SupervisorTickInput,
  route: RouteOutcomeResult,
  /** undefined (interrupted attempt, nothing journaled) ⇒ nothing to recover. */
  attemptFinishedSeq: number | undefined,
): Promise<RecoveryTickFields> {
  if (attemptFinishedSeq === undefined || !stoppedTicket(route)) return {};
  try {
    const outcomes = await materializePendingRecoveries({
      store: tickInput.store,
      controlRoot: tickInput.controlRoot,
      repoDir: tickInput.repoDir,
      gitRunner: tickInput.gitRunner,
      evaluatePolicy: tickInput.evaluatePolicy,
      missionId: tickInput.missionId,
      notify: tickInput.notify,
    });
    const mine = outcomes.find((o) => o.trigger_seq === attemptFinishedSeq);
    return {
      ...(mine?.recovery_ticket === undefined ? {} : { recovery_ticket: mine.recovery_ticket }),
      ...(mine?.recovery_skipped_reason === undefined
        ? {}
        : { recovery_skipped_reason: mine.recovery_skipped_reason }),
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    tickInput.notify?.(`Gỡ kẹt tự động lỗi (${message}) — tick sau quét lại từ journal`);
    return {};
  }
}
